/**
 * Extracts the buyer's registered (IMS) address from the purchase tickets.
 *
 * The backend attaches it per-ticket as `ticket.clientAddress` (camelized from
 * the `client_address` ticket meta — purchase INS-1053, via `TicketSerializer`).
 * It's the buyer's own registered address, so it's identical across tickets; we
 * return the first populated one. Returns `null` when no ticket carries it (e.g.
 * tenants/flows without the IMS lookup) — callers must not invent an address.
 *
 * Handles both normalized ways/ticket shapes the funnel produces:
 *   - connection tenants:     `way.tickets[]`
 *   - non-connection tenants: `way.fragments[].tickets[]`
 *
 * @param {object} purchase - Normalized purchase (from `usePurchase()`).
 * @returns {object|null} The registered address (`zip`, `stateCode`, `city`,
 *   `street`, `number`, `complement`, `neighborhood`, …) or `null`.
 */
export default function getRegisteredClientAddress(purchase) {
  if (!purchase) return null;

  const ways = [purchase.departs, purchase.returns].filter(Boolean);
  const tickets = ways.flatMap((way) => [
    ...(way.tickets || []),
    ...(way.fragments || []).flatMap((fragment) => fragment?.tickets || []),
  ]);

  const ticketWithAddress = tickets.find(
    (ticket) => ticket?.clientAddress && Object.keys(ticket.clientAddress).length > 0,
  );

  return ticketWithAddress?.clientAddress || null;
}
